import { useState } from "react";
import {
  AiGeneratedFlag,
  Button,
  ConfidenceBadge,
  FailClosedNotice,
  TextInput,
} from "@odingard/design-system";
import { ApiError, type QaResult } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { CaiError } from "../../components/CaiError";

interface Turn {
  question: string;
  result?: QaResult;
  error?: string;
  pending?: boolean;
}

const SUGGESTIONS = [
  "What are our confidentiality obligations?",
  "Which contracts have unlimited liability?",
  "What are the termination terms?",
];

export function CaiAsk() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [question, setQuestion] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const [busy, setBusy] = useState(false);
  const [fatal, setFatal] = useState<Error | null>(null);

  async function ask(q: string) {
    const query = q.trim();
    if (!query || busy) return;
    setQuestion("");
    setBusy(true);
    setTurns((t) => [...t, { question: query, pending: true }]);
    try {
      const result = await api.askContracts(orgId, query);
      setTurns((t) => t.map((turn, i) => (i === t.length - 1 ? { question: query, result } : turn)));
    } catch (e) {
      if (e instanceof ApiError && (e.code === "entitlement_denied" || e.status === 403)) {
        setFatal(e);
        return;
      }
      setTurns((t) => t.map((turn, i) => (i === t.length - 1 ? { question: query, error: e instanceof Error ? e.message : "Failed" } : turn)));
    } finally {
      setBusy(false);
    }
  }

  if (fatal) {
    return (
      <>
        <div className="page-head">
          <h1>Ask Your Contracts</h1>
        </div>
        <CaiError error={fatal} />
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Ask Your Contracts</h1>
          <p>Grounded portfolio Q&amp;A. Every answer cites its sources. Unsupported questions are declined, not guessed.</p>
        </div>
      </div>

      <div className="chat-log">
        {turns.length === 0 && (
          <div className="ods-stack">
            <p style={{ color: "var(--ods-color-muted)" }}>Try asking:</p>
            <div className="ods-row" style={{ flexWrap: "wrap" }}>
              {SUGGESTIONS.map((s) => (
                <Button key={s} size="sm" variant="secondary" onClick={() => ask(s)}>
                  {s}
                </Button>
              ))}
            </div>
          </div>
        )}

        {turns.map((turn, i) => (
          <div key={i} className="ods-stack">
            <div className="chat-msg chat-msg--user">{turn.question}</div>
            <div className="chat-msg chat-msg--ai">
              {turn.pending && <span style={{ color: "var(--ods-color-muted)" }}>Searching your contracts…</span>}
              {turn.error && <span style={{ color: "var(--ods-color-danger)" }}>{turn.error}</span>}
              {turn.result && !turn.result.grounded && (
                <FailClosedNotice
                  message={
                    turn.result.message ??
                    "No supporting evidence was found in your contracts. Contract AI does not answer without grounded citations."
                  }
                />
              )}
              {turn.result && turn.result.grounded && (
                <div>
                  <div className="ods-row" style={{ justifyContent: "space-between" }}>
                    <AiGeneratedFlag>AI answer · grounded</AiGeneratedFlag>
                    {typeof turn.result.confidence === "number" && <ConfidenceBadge value={turn.result.confidence} />}
                  </div>
                  <p style={{ marginTop: "var(--ods-space-3)" }}>{turn.result.answer}</p>
                  <div className="citation-list">
                    <strong style={{ fontSize: "var(--ods-text-xs)", textTransform: "uppercase", color: "var(--ods-color-muted)" }}>Sources</strong>
                    {turn.result.citations.map((c, j) => (
                      <div key={j} className="citation-src">
                        <strong>{c.contractTitle}</strong> · {c.locator}
                        <div style={{ marginTop: 4, color: "var(--ods-color-muted)" }}>{c.snippet}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          ask(question);
        }}
        className="ods-row"
        style={{ marginTop: "var(--ods-space-5)", position: "sticky", bottom: 0 }}
      >
        <div style={{ flex: 1 }}>
          <TextInput
            placeholder="Ask a question about your contracts…"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            aria-label="Ask a question"
            disabled={busy}
          />
        </div>
        <Button type="submit" disabled={busy || !question.trim()}>
          Ask
        </Button>
      </form>
    </>
  );
}
